import api from "../apiClient";

/* BOOKING API — lane B's endpoints, wrapped so the composer never has to think
   about HTTP.

   THE ONE RULE THIS FILE EXISTS TO ENFORCE: a business refusal arrives as
   200 with ok:false, and a malformed request arrives as 4xx, which axios
   throws. Two different shapes for "it did not happen" is how a caller ends up
   treating a thrown 400 as falsy-but-fine. So every function here resolves —
   never rejects — with the same {ok, code, detail} shape, and the composer
   branches on `ok` alone.

   AND THE COROLLARY, which is the important half: A FAILURE MUST NEVER READ AS
   "FREE". The contract is emphatic that `venue_unknown` is a refusal and not a
   pass, because "we could not find that room" and "that room is available"
   must not produce the same outcome. The same is true of a 500, a dropped
   connection or an expired token: if we could not determine the room is free,
   the honest answer is "could not check", never silence that the UI renders as
   a clear date. Every catch below returns ok:false for that reason.

   Codes this file adds to lane B's set (they cannot collide — B's are
   business codes, these are transport):
     unreachable   the request did not complete; conflict state is UNKNOWN
     bad_request   a 4xx; B's detail is passed through when present */

const transportFailure = (err, fallback) => {
  const res = err?.response;
  const body = res?.data;
  // Lane B's 400s carry their own {code, detail} naming the offending field —
  // far better than anything invented here, so prefer them.
  if (body && typeof body === "object" && typeof body.detail === "string" && body.detail) {
    return { ok: false, code: body.code || "bad_request", detail: body.detail, conflicts: [] };
  }
  if (res?.status === 401 || res?.status === 403) {
    return {
      ok: false,
      code: "unreachable",
      // Name the cause. "Could not check" sends a rep hunting a booking bug
      // that is really an expired session.
      detail: "Your session has expired, so the booking could not be checked. Sign in again.",
      conflicts: [],
    };
  }
  return { ok: false, code: "unreachable", detail: fallback, conflicts: [] };
};

/* A 200 body that is not the shape we expect is also not a pass. An endpoint
   that answers with HTML (a proxy error page) or omits `ok` has told us
   nothing, and defaulting a missing `ok` to true is how a composer reports a
   room is free because a load balancer returned a 200. */
const readBody = (data, fallback) => {
  if (!data || typeof data !== "object" || typeof data.ok !== "boolean") {
    return { ok: false, code: "unreachable", detail: fallback, conflicts: [] };
  }
  return data;
};

/* ---------------------------------------------------------------------------
   Venues (the space picker)

   TWO ENDPOINTS, AND THE GOOD ONE IS NOT DEPLOYED YET. Measured against prod
   on 2026-10-02, not taken from a deploy report:
     GET /api/appointments/booking/venues/   404  <- B's three-field picker
     GET /api/events/venues/                 403  <- pre-existing full CRUD
   403 means the route is there and wants auth; 404 means it is not.

   So this tries B's endpoint and falls back to the CRUD one, normalising both
   to the same three fields. They read the SAME events_app.Venue rows, so the
   ids are interchangeable and nothing built on them changes when B's leg
   lands — at which point this silently starts using the lighter endpoint with
   no edit here.

   The fallback is not a workaround to leave lying around: when
   booking/venues/ has been live for a while, delete `fromCrudShape` and the
   second call.                                                            */

const fromPickerShape = (v) => ({
  id: v?.id,
  name: String(v?.name || "").trim(),
  capacity: v?.capacity ?? null,
  active: v?.active !== false,
});

const fromCrudShape = (v) => ({
  id: v?.id,
  name: String(v?.name || "").trim(),
  // The CRUD serializer carries twenty fields; the picker wants three. Capacity
  // is spelled differently there, so read both rather than show a blank.
  capacity: v?.capacity ?? v?.max_capacity ?? null,
  active: v?.is_active !== false && v?.active !== false,
});

const rowsOf = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.results) ? d.results : Array.isArray(d?.venues) ? d.venues : [];

export async function getVenues({ all = false } = {}) {
  try {
    const res = await api.get("/appointments/booking/venues/", { params: all ? { all: 1 } : {} });
    const body = res?.data;
    if (body && typeof body === "object" && body.ok === true) {
      return { ok: true, venues: rowsOf(body).map(fromPickerShape).filter((v) => v.id != null) };
    }
    // Answered but not ok — fall through to the CRUD endpoint rather than
    // showing an empty picker.
  } catch {
    /* 404 today. Fall through. */
  }

  try {
    const res = await api.get("/events/venues/");
    const venues = rowsOf(res?.data).map(fromCrudShape).filter((v) => v.id != null);
    return { ok: true, venues: all ? venues : venues.filter((v) => v.active) };
  } catch (err) {
    // No picker. The composer falls back to a typed space name and tells the
    // rep that an ambiguous name will be refused — it does not pretend the
    // tenant has no venues.
    return { ...transportFailure(err, "The list of spaces could not be loaded."), venues: null };
  }
}

/* ---------------------------------------------------------------------------
   1. Space conflict — read-only, writes nothing, safe on every change.      */

export async function checkSpaceConflict(body) {
  try {
    const res = await api.post("/appointments/booking/space-conflict/", body);
    return readBody(
      res?.data,
      "The space could not be checked, so it is not known whether it is free.",
    );
  } catch (err) {
    return transportFailure(
      err,
      "The space could not be checked, so it is not known whether it is free.",
    );
  }
}

/* ---------------------------------------------------------------------------
   2. Event hold — creates the booking. Runs the conflict check itself first
   and writes NOTHING on a refusal, so ok:false needs no cleanup.            */

export async function createEventHold(body) {
  try {
    const res = await api.post("/appointments/booking/event-hold/", body);
    return readBody(res?.data, "The booking did not go through.");
  } catch (err) {
    // A transport failure here is genuinely ambiguous: the write may or may not
    // have landed. Say that, rather than "it failed" — a rep told it failed
    // will book a second time.
    return transportFailure(
      err,
      "The booking could not be confirmed. Check the bookings list before trying again.",
    );
  }
}

/* ---------------------------------------------------------------------------
   3. Visit series — the recurring plan.

   PARTLY DEPLOYED. Live today: quote_id-driven, with window_start/window_end
   as explicit times. NOT live: customer_id + offering_id, and `arrival_window`
   with the tenant's grid. The customer card has no quote, so the customer-driven
   call is the one it needs, and it will come back 400 until B's leg lands.
   The composer feature-detects rather than hiding the control, and says which
   it is — see `fetchArrivalGrid`.                                           */

export async function createVisitSeries(body) {
  try {
    const res = await api.post("/appointments/booking/visit-series/", body);
    return readBody(res?.data, "The visits were not scheduled.");
  } catch (err) {
    return transportFailure(err, "The visits were not scheduled.");
  }
}

/* Fetch the tenant's arrival-window grid for a date.

   This is B's trick and it is a good one: `offered_windows` comes back on
   SUCCESS and on dry_run too, so a dry run with no arrival_window returns the
   grid. That turns the window field from a text box a rep can get wrong into
   a row of buttons they cannot — the grid refusal becomes a backstop instead
   of an error a human sees.

   Two things make this safe to call against a build that does not support it:
   dry_run writes nothing, and an unsupported build 400s, which lands in the
   catch and yields windows:null. null means "unknown, use a text field" and is
   deliberately different from [] which means "this tenant has no grid" —
   default-open, per the contract: a tenant with no business hours configured
   has no grid and nothing is validated, so an empty row of buttons must not be
   the thing that stops them scheduling. */
export async function fetchArrivalGrid({ customerId, offeringId, startDate, frequency }) {
  const res = await createVisitSeries({
    customer_id: customerId,
    offering_id: offeringId || undefined,
    frequency: frequency || "weekly",
    start_date: startDate || undefined,
    dry_run: true,
  });
  if (!Array.isArray(res?.offered_windows)) return { windows: null, res };
  return { windows: res.offered_windows, res };
}
